export type TrackType = 'synth' | 'drum' | 'audio' | 'midi'

export interface Track {
  id: string
  name: string
  type: TrackType
  enabled: boolean
  muted: boolean
  soloed: boolean
  volume: number
  pan: number
  instrumentId: string
  color: string
  steps: Step[]
  active: boolean
  params: Record<string, number>
}

export interface Step {
  index: number
  enabled: boolean
  velocity: number
  note: string
  length: number
}

export interface Sequence {
  trackId: string
  steps: Step[]
}

export type InstrumentType = 'SubSynth' | 'Beatbox' | 'PCMSynth' | 'Bassline' | 'PadSynth' | 'SbitSynth' | 'Modular' | 'Organ' | 'Vocoder' | 'FMSynth'

export type ModuleType = 'instrument' | 'effect' | 'master'

export interface Module {
  id: string
  name: string
  type: ModuleType
  parameters: Record<string, number>
  enabled: boolean
  selected: boolean
}

export interface Instrument extends Module {
  instrumentType: InstrumentType
}

export interface Effect extends Module {
  effectType: EffectType
}

export type EffectType = 'reverb' | 'delay' | 'filter' | 'distortion' | 'chorus' | 'limiter'

export interface PatchCable {
  id: string
  from: string
  to: string
  color: string
}

export interface TransportState {
  isPlaying: boolean
  isRecording: boolean
  tempo: number
  timeSignature: [number, number]
  position: number
  loop: boolean
  masterVolume: number
  masterPan: number
  limiter: boolean
}

export interface MasterState {
  volume: number
  pan: number
  limiter: boolean
}

export type PatternPreset = 'clear' | 'random' | 'kick' | 'hat' | 'bass'

export type PatchPoint = 'L' | 'R' | 'Input' | 'Output' | 'Pitch' | 'Amp' | 'Filter Cut' | 'Filter Res' | 'Morph' | 'Ring' | 'Mod' | 'Retrigger'

export interface AppState {
  tracks: Track[]
  modules: Module[]
  patches: PatchCable[]
  transport: TransportState
  selectedTrackId: string | null
  selectedModuleId: string | null
  selectedModuleType: ModuleType
  patternName: string
}

export interface AppStore extends AppState {
  addTrack: () => void
  removeTrack: (id: string) => void
  updateTrack: (id: string, updates: Partial<Track>) => void
  toggleStep: (trackId: string, stepIndex: number) => void
  setStepVelocity: (trackId: string, stepIndex: number, velocity: number) => void
  clearSequence: (trackId: string) => void
  randomizeSequence: (trackId: string) => void
  applyPreset: (trackId: string, preset: PatternPreset) => void
  toggleTransport: () => void
  toggleRecord: () => void
  stopTransport: () => void
  setTempo: (tempo: number) => void
  setPosition: (position: number) => void
  setLoop: (loop: boolean) => void
  updateModuleParameter: (moduleType: ModuleType, moduleId: string, key: string, value: number) => void
  toggleModule: (id: string) => void
  addPatch: (from: PatchPoint, to: PatchPoint) => void
  clearPatches: () => void
  updateMaster: (updates: Partial<MasterState>) => void
  selectTrack: (id: string) => void
  selectModule: (moduleType: ModuleType, moduleId: string) => void
}

export const MODULE_PARAMETERS: Record<string, { key: string; label: string; min: number; max: number; step: number }> = {
  depth: { key: 'depth', label: 'Depth', min: 0, max: 1, step: 0.01 },
  rate: { key: 'rate', label: 'Rate', min: 0, max: 1, step: 0.01 },
  freq: { key: 'freq', label: 'Freq', min: 0, max: 1, step: 0.01 },
  reso: { key: 'reso', label: 'Reso', min: 0, max: 1, step: 0.01 },
  damp: { key: 'damp', label: 'Damp', min: 0, max: 1, step: 0.01 },
  wet: { key: 'wet', label: 'Wet', min: 0, max: 1, step: 0.01 },
  delay: { key: 'delay', label: 'Delay', min: 0, max: 1, step: 0.01 },
  width: { key: 'width', label: 'Width', min: 0, max: 1, step: 0.01 },
  attack: { key: 'attack', label: 'Attack', min: 0, max: 1, step: 0.01 },
  release: { key: 'release', label: 'Release', min: 0, max: 1, step: 0.01 },
  threshold: { key: 'threshold', label: 'Threshold', min: 0, max: 1, step: 0.01 },
  ratio: { key: 'ratio', label: 'Ratio', min: 0, max: 1, step: 0.01 },
}

export const PREFERRED_PARAMETERS = [
  'depth',
  'rate',
  'freq',
  'reso',
  'damp',
  'wet',
  'delay',
  'width',
  'attack',
  'release',
  'threshold',
  'ratio',
]

export const INSTRUMENT_TYPES: InstrumentType[] = [
  'SubSynth',
  'Beatbox',
  'PCMSynth',
  'Bassline',
  'PadSynth',
  'SbitSynth',
  'Modular',
  'Organ',
  'Vocoder',
  'FMSynth',
]

export const EFFECT_TYPES: EffectType[] = ['reverb', 'delay', 'filter', 'distortion', 'chorus', 'limiter']
